<script setup lang="ts">
	import { computed, inject, ref, watch } from 'vue'
	import { useToast } from 'vue-toastification'

	import enlaceIsotipoExample from '@/assets/imgs/icono_enlace.png'
	import enlaceLogoExample from '@/assets/imgs/logo-png-eb.png'
	import HasPermission from '@/components/HasPermission.vue'
	import ImageCropper from '@/components/ImageCropper.vue'
	import LoadingCircle from '@/components/icons/LoadingCircle.vue'

	import useCompanyMedia from '@/modules/companies/composables/useCompanyMedia'
	import { Company } from '@/modules/companies/interfaces/company'

	interface Props {
		company: Company
		namePermissionUpdate?: string
		entityType?: string
		collection?: 'logo' | 'isotipo'
		title?: string
		description?: string
		exampleText?: string
	}

	const props = withDefaults(defineProps<Props>(), {
		collection: 'logo',
		title: 'Logo de la Empresa',
	})

	const { companyMediaCreateAsync } = useCompanyMedia()
	const toast = useToast()
	const ALLOWED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif']
	const MAX_FILE_SIZE = 20 * 1024 * 1024
	const logoFile = ref<File | null>(null)
	const updatingLogo = ref(false)
	const addingLogo = ref(false)
	const dragOver = ref(false)
	const imagePreview = ref<string | null>(null)
	const showCropperModal = ref(false)

	const { invalidateQueryData } = inject('editCompanyProvides') as {
		invalidateQueryData: () => Promise<void>
		isFetching: boolean
	}

	// Computed: URL actual según la colección (logo o isotipo)
	const currentImageUrl = computed(() =>
		props.collection === 'isotipo' ? props.company?.isotipo : props.company?.logo
	)

	// Si la URL de la imagen falla (404/403), cae al fallback de iniciales
	const logoLoadError = ref(false)
	watch(currentImageUrl, () => {
		logoLoadError.value = false
	})

	// El backend cae al logo genérico de Cloudinary cuando la empresa no subió
	// nada: no cuenta como "tiene logo", si no se muestra ese ícono feo como si
	// fuera el de la empresa.
	const isDefaultImage = computed(() =>
		(currentImageUrl.value ?? '').includes('default_company_logo.png')
	)

	// Computed para determinar si hay imagen actual
	const hasCurrentLogo = computed(() => {
		return (
			!!currentImageUrl.value &&
			currentImageUrl.value.length > 0 &&
			!logoLoadError.value &&
			!isDefaultImage.value
		)
	})

	const isSquare = computed(() => props.collection === 'isotipo')

	const isValidImageFile = (file: File): boolean => {
		if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
			toast.error('Formato no permitido. Usa PNG, JPG, JPEG o GIF.')
			return false
		}
		if (file.size > MAX_FILE_SIZE) {
			toast.error('La imagen supera el límite de 20 MB.')
			return false
		}
		return true
	}

	// Función para generar vista previa de la imagen y abrir el cropper
	const generateImagePreview = (file: File) => {
		const reader = new FileReader()
		reader.onload = (e) => {
			imagePreview.value = e.target?.result as string
			showCropperModal.value = true
		}
		reader.readAsDataURL(file)
	}

	const handleLogoSubmit = async () => {
		updatingLogo.value = true
		if (logoFile.value) {
			try {
				await companyMediaCreateAsync({
					params: {
						companyId: props.company?.id,
						media: logoFile.value,
						collection: props.collection,
					},
					config: {
						onSuccessToast: `${props.title} actualizado exitosamente`,
						raiseToast: true,
					},
				})
				await invalidateQueryData()
				addingLogo.value = false
				imagePreview.value = null
				logoFile.value = null
			} catch (err) {
				console.log(err)
			} finally {
				updatingLogo.value = false
			}
		}
	}

	const onCropApplied = async (file: File) => {
		logoFile.value = file
		showCropperModal.value = false
		await handleLogoSubmit()
	}

	const onCropCancelled = () => {
		showCropperModal.value = false
	}

	// Manejar drag and drop
	const handleDragOver = (e: DragEvent) => {
		e.preventDefault()
		dragOver.value = true
	}

	const handleDragLeave = (e: DragEvent) => {
		e.preventDefault()
		dragOver.value = false
	}

	const handleDrop = (e: DragEvent) => {
		e.preventDefault()
		dragOver.value = false
		const files = e.dataTransfer?.files
		if (files && files.length > 0) {
			const file = files[0]
			if (isValidImageFile(file)) {
				logoFile.value = file
				generateImagePreview(file)
			}
		}
	}

	// Manejar selección de archivo
	const handleFileSelect = (event: Event) => {
		const target = event.target as HTMLInputElement
		const file = target.files?.[0]
		if (file && isValidImageFile(file)) {
			logoFile.value = file
			generateImagePreview(file)
		}
	}

	const cancelEditing = () => {
		addingLogo.value = false
		logoFile.value = null
		imagePreview.value = null
		showCropperModal.value = false
	}

	watch(addingLogo, (value) => {
		if (!value) {
			logoFile.value = null
			imagePreview.value = null
			showCropperModal.value = false
		}
	})
</script>

<template>
	<div
		class="flex flex-col items-center gap-5 p-6 bg-white rounded-2xl border border-gray-200 shadow-sm"
	>
		<!-- Header -->
		<div class="text-center">
			<h3 class="text-base font-semibold text-gray-900 mb-1">
				{{ title }}
			</h3>
			<p class="text-sm text-gray-500">
				{{
					addingLogo ?
						'Selecciona una nueva imagen'
					:	description || 'Gestiona la imagen de tu empresa'
				}}
			</p>
		</div>

		<!-- Ejemplo visual: qué se espera subir aquí (logo/isotipo real de Enlace) -->
		<div
			v-if="!addingLogo"
			class="flex items-center gap-3 rounded-xl bg-gray-50 border border-gray-100 px-3 py-2.5 w-full max-w-xs"
		>
			<div
				class="flex-shrink-0 flex items-center justify-center rounded-lg bg-white border border-gray-200 p-1.5"
				:class="isSquare ? 'h-9 w-9' : 'h-9 w-[90px]'"
			>
				<img
					:src="isSquare ? enlaceIsotipoExample : enlaceLogoExample"
					alt="Ejemplo"
					class="max-h-full max-w-full object-contain"
				/>
			</div>
			<p class="text-[11px] leading-snug text-gray-500 text-left">
				<span class="font-medium text-gray-700">Ejemplo: </span>
				{{
					exampleText ||
					(isSquare ?
						'solo el ícono o símbolo de la marca, sin texto.'
					:	'ícono + nombre de la empresa.')
				}}
			</p>
		</div>

		<!-- Logo Display / Preview Area -->
		<div class="relative">
			<!-- Current Logo or Placeholder -->
			<div
				v-if="!addingLogo"
				class="relative group transition-all duration-300"
			>
				<div
					:class="[
						'rounded-xl overflow-hidden flex items-center justify-center transition-colors',
						isSquare ? 'w-28 h-28' : 'w-44 h-28',
						hasCurrentLogo ?
							'bg-white border border-gray-200'
						:	'bg-gray-50 border-2 border-dashed border-gray-200 group-hover:border-blue-300',
					]"
				>
					<img
						v-if="hasCurrentLogo"
						:src="currentImageUrl"
						:alt="`${title} de ${company.name}`"
						class="max-w-full max-h-full object-contain p-2"
						@error="logoLoadError = true"
					/>
					<div
						v-else
						class="flex flex-col items-center justify-center gap-1.5 text-gray-400"
					>
						<svg
							class="w-6 h-6"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="1.5"
								d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14M4 8h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
							></path>
						</svg>
						<span class="text-[11px] font-medium">Sin {{ title.toLowerCase() }}</span>
					</div>
				</div>

				<!-- Overlay on hover -->
				<HasPermission
					:name-permission="namePermissionUpdate || ''"
					:entity-type="entityType || ''"
				>
					<div
						class="absolute inset-0 bg-black bg-opacity-40 rounded-xl flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 cursor-pointer"
						@click="addingLogo = true"
					>
						<svg
							class="w-7 h-7 text-white"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
							></path>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
							></path>
						</svg>
					</div>
				</HasPermission>
			</div>

			<!-- Drag & Drop / Selection Area -->
			<div
				v-if="addingLogo"
				class="relative"
				@dragover="handleDragOver"
				@dragleave="handleDragLeave"
				@drop="handleDrop"
			>
				<div
					:class="[
						'border-2 border-dashed rounded-xl p-8 text-center transition-all duration-300',
						dragOver ?
							'border-blue-400 bg-blue-50'
						:	'border-gray-300 hover:border-blue-400 hover:bg-gray-50',
						logoFile ? 'border-green-400 bg-green-50' : '',
					]"
				>
					<input
						type="file"
						accept="image/*"
						@change="handleFileSelect"
						class="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
					/>

					<div class="space-y-4">
						<div
							class="mx-auto w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center"
						>
							<svg
								class="w-6 h-6 text-gray-600"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
								></path>
							</svg>
						</div>

						<div>
							<p class="text-sm font-medium text-gray-700">
								{{
									dragOver ? '¡Suelta la imagen aquí!'
									: logoFile ? logoFile.name
									: 'Arrastra una imagen o haz clic para seleccionar'
								}}
							</p>
							<p class="text-xs text-gray-500 mt-1">
								PNG, JPG, JPEG o GIF hasta 20 MB
							</p>
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- Action Buttons -->
		<div class="flex flex-col justify-center sm:flex-row gap-3 w-full max-w-sm">
			<!-- Edit Mode Controls -->
			<template v-if="addingLogo">
				<button
					type="button"
					@click="cancelEditing"
					class="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 transition-colors duration-200"
				>
					Cancelar
				</button>
			</template>

			<!-- Default Controls -->
			<template v-else>
				<HasPermission
					:name-permission="namePermissionUpdate || ''"
					:entity-type="entityType || ''"
				>
					<button
						@click="addingLogo = true"
						class="flex-1 px-6 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-all duration-200 flex items-center justify-center gap-2 shadow-md hover:shadow-lg transform hover:scale-105"
					>
						<svg
							class="w-4 h-4"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
							></path>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
							></path>
						</svg>
						{{ hasCurrentLogo ? `Cambiar ${title}` : `Agregar ${title}` }}
					</button>
				</HasPermission>
			</template>
		</div>

		<!-- Status Messages -->
		<div
			v-if="updatingLogo"
			class="text-center"
		>
			<div class="flex items-center justify-center gap-2 text-blue-600">
				<LoadingCircle class="!h-5 !w-5" />
				<span class="text-sm font-medium">Actualizando logo...</span>
			</div>
		</div>

		<!-- Crop Modal -->
		<ImageCropper
			v-model:isVisible="showCropperModal"
			:image-src="imagePreview || ''"
			:uploading="updatingLogo"
			crop-mode="free"
			:aspect-ratio-locked="false"
			:flatten-to-white="true"
			@crop-applied="onCropApplied"
			@crop-cancelled="onCropCancelled"
		/>
	</div>
</template>

<style scoped>
	/* Animaciones y transiciones suaves */
	.transition-all {
		transition-property: all;
		transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
	}

	.duration-200 {
		transition-duration: 200ms;
	}

	.duration-300 {
		transition-duration: 300ms;
	}

	/* Efectos hover para botones */
	.transform:hover {
		transform: scale(1.05);
	}

	.hover\:scale-105:hover {
		transform: scale(1.05);
	}

	/* Shadows personalizadas */
	.shadow-sm {
		box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
	}

	.shadow-md {
		box-shadow:
			0 4px 6px -1px rgba(0, 0, 0, 0.1),
			0 2px 4px -1px rgba(0, 0, 0, 0.06);
	}

	.shadow-lg {
		box-shadow:
			0 10px 15px -3px rgba(0, 0, 0, 0.1),
			0 4px 6px -2px rgba(0, 0, 0, 0.05);
	}

	.hover\:shadow-lg:hover {
		box-shadow:
			0 10px 15px -3px rgba(0, 0, 0, 0.1),
			0 4px 6px -2px rgba(0, 0, 0, 0.05);
	}

	/* Efectos para el área de drag & drop */
	.border-dashed {
		border-style: dashed;
	}

	/* Estados de hover específicos */
	.group:hover .group-hover\:opacity-100 {
		opacity: 1;
	}

	/* Espaciado */
	.space-y-4 > :not([hidden]) ~ :not([hidden]) {
		margin-top: 1rem;
	}

	/* Responsive design */
	@media (max-width: 640px) {
		.flex-col.sm\:flex-row {
			flex-direction: column;
		}

		.max-w-sm {
			max-width: 100%;
		}
	}

	/* Mejoras visuales para el input file */
	input[type='file'] {
		cursor: pointer;
	}

	input[type='file']::-webkit-file-upload-button {
		visibility: hidden;
	}

	input[type='file']::before {
		content: '';
		display: inline-block;
		background: transparent;
		border: none;
		cursor: pointer;
	}
</style>
